import CryptoJS from 'crypto-js'
import { getSignKey } from './auth'

/**
 * 接口请求签名工具
 *
 * 签名规则：
 *   签名原文 = HTTP_METHOD\nREQUEST_URI\nTimestamp\nNonce
 *   Sign     = Base64Url(HMAC-SHA256(signKey, 签名原文))
 *
 * 登录前（xlogin / wxcp/doLogin）用初始化接口返回的 loginSignKey 签名，
 * 并额外携带 Login-Ticket 请求头；登录成功后所有业务接口用返回的 signKey 签名。
 */

// 将 HMAC/哈希结果的 WordArray 转成 Base64Url 字符串
export function base64UrlEncode(wordArray) {
  return CryptoJS.enc.Base64.stringify(wordArray)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

// 生成 Nonce：Base64Url 随机字符串（24 字节 → base64 32 字符，符合 16~128 长度要求）
export function generateNonce() {
  return base64UrlEncode(CryptoJS.lib.WordArray.random(24))
}

/**
 * 计算 Sign
 * @param {string} signKey 签名密钥（临时 loginSignKey 或登录后 signKey）
 * @param {string} method HTTP 方法，如 GET / POST
 * @param {string} uri 请求 URI（不含 baseURL 前缀，如 /admin/order/detail）
 * @param {string} timestamp Unix 毫秒时间戳
 * @param {string} nonce 随机串
 */
export function generateSign(signKey, method, uri, timestamp, nonce) {
  const raw = [method, uri, timestamp, nonce].join('\n')
  return base64UrlEncode(CryptoJS.HmacSHA256(raw, signKey))
}

/**
 * 生成一组签名请求头
 * @returns {{Timestamp: string, Nonce: string, Sign: string}}
 */
export function buildSignHeaders(signKey, method, uri) {
  const timestamp = String(Date.now())
  const nonce = generateNonce()
  return {
    Timestamp: timestamp,
    Nonce: nonce,
    Sign: generateSign(signKey, method, uri, timestamp, nonce)
  }
}

// 登录阶段请求：用临时 loginSignKey 签名，并额外携带 Login-Ticket
const LOGIN_SIGN_URLS = ['/admin/auth/xlogin', '/admin/auth/wxcp/doLogin']

// 登录前接口（拿到 lt/lsk 之前调用），只走 Init-Time/Init-Sign，无需共享密钥
const INIT_SIGN_URLS = [
  '/admin/auth/login/check',
  '/admin/auth/login/xcode',
  '/admin/auth/wxcp/loginState'
]

const BASE_URL_PREFIX = '/api'

// 去掉 baseURL(/api) 前缀
function stripApiPrefix(url) {
  return url && url.indexOf(BASE_URL_PREFIX) === 0
    ? url.slice(BASE_URL_PREFIX.length)
    : url || ''
}

export function attachSignHeaders(config) {
  // 签名/判断用的 URI：去掉 baseURL(/api) 前缀
  const url = stripApiPrefix(config.url)
  // 登录前接口已由 attachInitSignHeaders 补 Init-Sign，无需叠加业务签名
  if (INIT_SIGN_URLS.indexOf(url) > -1) return config
  const { loginTicket, loginSignKey } = getLoginSign()
  // 登录阶段（xlogin / wxcp/doLogin）用临时 loginSignKey；其余已登录业务接口用正式 signKey
  const isLoginSign = LOGIN_SIGN_URLS.indexOf(url) > -1
  const signKey = isLoginSign ? loginSignKey : getSignKey()
  if (signKey) {
    const { Timestamp, Nonce, Sign } = buildSignHeaders(
      signKey,
      (config.method || 'get').toUpperCase(),
      url
    )
    config.headers['Timestamp'] = Timestamp
    config.headers['Nonce'] = Nonce
    config.headers['Sign'] = Sign
    if (isLoginSign && loginTicket) {
      config.headers['Login-Ticket'] = loginTicket
    }
  }
  return config
}

// ==================== 登录前接口签名（Init-Time / Init-Sign） ====================
//   source     = PATH + '|' + Init-Time    （PATH 为去掉 /api 前缀的请求路径）
//   Key        = MD5(source) 的 32 位小写 hex 字符串（HMAC 密钥）
//   Init-Sign  = Base64Url(HMAC-SHA256(Key, source))
/**
 * 生成一组登录前签名请求头（Init-Time / Init-Sign）
 * @param {string} path 去掉 /api 前缀的请求路径，如 /admin/auth/login/xcode
 */
export function buildInitSign(path) {
  const initTime = String(Date.now())
  const source = `${path}|${initTime}`
  const key = CryptoJS.MD5(source) // 取其 32 位 hex 字符串作为 HMAC 密钥
  return {
    'Init-Time': initTime,
    'Init-Sign': base64UrlEncode(CryptoJS.HmacSHA256(source, key.toString(CryptoJS.enc.Hex)))
  }
}

/** 为命中 INIT_SIGN_URLS 的请求补充 Init-Time / Init-Sign 请求头 */
export function attachInitSignHeaders(config) {
  const path = stripApiPrefix(config.url)
  if (INIT_SIGN_URLS.indexOf(path) > -1) {
    Object.assign(config.headers, buildInitSign(path))
  }
  return config
}

// ==================== 登录临时票据（loginTicket / loginSignKey） ====================
// 登录阶段使用，登录成功或票据过期后应清除。
let loginTicket = ''
let loginSignKey = ''

/** 保存登录临时票据与临时签名密钥（来自 xcode / wxcp/loginState 响应） */
export function setLoginSign(ticket, signKey) {
  loginTicket = ticket
  loginSignKey = signKey
}

/** 读取登录临时票据与临时签名密钥 */
export function getLoginSign() {
  return { loginTicket, loginSignKey }
}

/** 清除登录临时票据（登录成功 / 票据失效后调用） */
export function clearLoginSign() {
  loginTicket = ''
  loginSignKey = ''
}
